import { useEffect, useState } from 'react'
import { Check, Wand2, Sparkles, Image as ImageIcon, Maximize2, X } from 'lucide-react'
import type {
  SocialTemplate,
  SocialMediaRecommendedTemplateState,
} from '../../types'

/**
 * SocialTemplatePicker
 *
 * Inline picker rendered with each `SocialMediaRecommendationAgent`
 * chat turn (see `type: 'social_templates'` + `ref.social_template_ids`
 * on the chat history item). Structurally parallels `ConceptPicker`:
 * the parent chat message provides the ordered `socialTemplateIds` for
 * *this* turn; we resolve them against the live
 * `social_media_recommended_template.items` so per-item status stays
 * fresh across refine / finalize transitions on old turns.
 *
 * We keep this as a dedicated component (not a variant of
 * ConceptPicker) because the two flows are likely to diverge in
 * copy, card metadata (format_id badges, aspect ratios) and future
 * per-surface actions. The visual language stays intentionally
 * identical for now so users pattern-match between the two pickers.
 */

/**
 * Actions the social picker can emit. Intentionally narrower than
 * `ConceptAction`:
 *   • `finalize`     — user picks a template from the grid.
 *   • `generate_more`— user wants *new* templates via AI, and will
 *     describe what they want in the composer (Refine-style chip).
 * There is deliberately no per-card `refine` (the BE doesn't refine an
 * individual social template) and no `skip` (social picker only closes
 * on a finalize).
 */
export type SocialTemplateAction =
  | { kind: 'finalize'; templateId: string; templateTitle?: string }
  | { kind: 'generate_more' }

export interface SocialTemplatePickerProps {
  /** Ordered ids referenced by this specific SocialMediaRecommendationAgent turn. */
  templateIds: string[]
  /** Live `response_state.social_media_recommended_template` — resolves
   *  ids to items and drives phase-based locking. */
  state?: SocialMediaRecommendedTemplateState | null
  onAction: (action: SocialTemplateAction) => void
  isBusy?: boolean
}

export function SocialTemplatePicker({
  templateIds,
  state,
  onAction,
  isBusy = false,
}: SocialTemplatePickerProps) {
  const [lightbox, setLightbox] = useState<{
    src: string
    title?: string
  } | null>(null)

  useEffect(() => {
    if (!lightbox) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  const byId = new Map<string, SocialTemplate>()
  for (const item of state?.items ?? []) {
    if (item?.id) byId.set(item.id, item)
  }

  const cards: SocialTemplate[] = templateIds
    .map((id) => byId.get(id))
    .filter((c): c is SocialTemplate => Boolean(c))

  if (cards.length === 0) return null

  const phase = state?.phase
  // Prefer the explicit BE selection field; fall back to any item
  // marked `finalized` so a mid-stream schema drift on that key never
  // hides the winning card.
  const selectedId =
    state?.selected_id ??
    (state as any)?.selected_template_id ??
    cards.find((c) => c.status === 'finalized')?.id ??
    null

  // The BE indicates a chosen template two independent ways:
  //   • `phase: 'finalized' | 'skipped'`
  //   • `selected_id` populated (may arrive before phase flips)
  // Either signal alone should lock the picker so once the user
  // picks a card every other card's Use-this button disables and
  // non-chosen tiles dim — parity with the ConceptPicker "phase
  // change wins" behavior even when phase hasn't updated yet.
  const isLocked =
    phase === 'finalized' || phase === 'skipped' || selectedId != null

  const buttonsDisabled = isLocked || isBusy

  // Card thumb is always an image. `screenshot` is the BE's static
  // image field; `preview_url` is an HTML template page (not an
  // image) so we intentionally do NOT fall back to it here — using
  // it as an <img src> would 404 or render nothing. `thumbnail_url`
  // is kept as a legacy fallback for older payloads.
  const resolveThumb = (card: SocialTemplate): string =>
    (typeof card.screenshot === 'string' && card.screenshot) ||
    (typeof (card as any).thumbnail_url === 'string' && (card as any).thumbnail_url) ||
    ''

  return (
    <>
      <div className="mt-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map((card) => {
            const isChosen = selectedId != null && card.id === selectedId
            const dim = isLocked && !isChosen
            const thumb = resolveThumb(card)
            const blurb =
              (typeof card.description === 'string' && card.description) ||
              (typeof card.summary === 'string' && card.summary) ||
              ''
            return (
              <div
                key={card.id}
                className={`group relative rounded-xl border transition-all overflow-hidden bg-white dark:bg-gray-900 ${
                  isChosen
                    ? 'border-primary-500 ring-2 ring-primary-500/30 shadow-md'
                    : 'border-gray-200 dark:border-gray-800 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm'
                } ${dim ? 'opacity-50' : ''}`}
              >
                <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  {thumb ? (
                    <button
                      type="button"
                      onClick={() =>
                        setLightbox({
                          src: thumb,
                          title: card.title || `Template ${card.id}`,
                        })
                      }
                      className="group/thumb absolute inset-0 w-full h-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                      aria-label={`Enlarge ${card.title || `template ${card.id}`}`}
                    >
                      <img
                        src={thumb}
                        alt={card.title || card.id}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover/thumb:scale-[1.03]"
                        loading="lazy"
                        onError={(e) => {
                          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/25 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100 text-[10px] font-semibold shadow">
                          <Maximize2 className="h-3 w-3" />
                          View
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}

                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {isChosen && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary-600 text-white shadow-sm">
                        <Check className="h-3 w-3" />
                        Selected
                      </span>
                    )}
                    {card.status === 'refined' && !isChosen && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-500 text-white shadow-sm">
                        <Wand2 className="h-3 w-3" />
                        Refined
                      </span>
                    )}
                  </div>

                  {/* Format badge (top-right) — distinguishes twitter /
                      instagram / etc. surfaces at a glance. Only rendered
                      when the BE supplies a `format_id`. */}
                  {typeof card.format_id === 'string' && card.format_id && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-gray-900/80 text-white shadow-sm">
                        {card.format_id.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-3 space-y-2">
                  <h4
                    className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2"
                    title={card.title || card.id}
                  >
                    {card.title || `Template ${card.id}`}
                  </h4>
                  {blurb && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
                      {blurb}
                    </p>
                  )}

                  {!isLocked && (
                    <div className="flex items-center gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={() =>
                          onAction({
                            kind: 'finalize',
                            templateId: card.id,
                            templateTitle: card.title,
                          })
                        }
                        disabled={buttonsDisabled}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Check className="h-3 w-3" />
                        Use this
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Grid-level action. There is no "Skip" for the social picker
            — the flow only closes on finalize. "Generate more with AI"
            behaves like a Refine chip: Home stages a composer chip so
            the user can describe what they want before sending. */}
        {!isLocked && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onAction({ kind: 'generate_more' })}
              disabled={buttonsDisabled}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:border-primary-300 dark:hover:border-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Generate more with AI
            </button>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.title || 'Template preview'}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.title || 'Template preview'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {lightbox.title && (
              <div className="text-white text-sm font-semibold text-center max-w-full truncate px-4">
                {lightbox.title}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
